import React, { useEffect } from 'react';
import { Box, Typography, useMediaQuery, FormControlLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GlobalStyles } from '@mui/system';

import BulletList from './bullet-list';
import NumberList from './number-list';
import Model3DViewer from './model3DViewer';
import Callout from './callout';
import HandoutImageLoader from './handout-image-loader';
import IconStrip from './icon-strip';
import Whiteboard from './whiteboard/Whiteboard';
import AntSwitch from '../../UIControls/AntSwitch';
import DisclaimerNotice from './disclaimer-notice';
import Copyright from './copyright';

function DisplayHandoutEmbed({
    handoutData,
    showLabels,
    setShowLabels,
    show3DModel,
    showMainIllustration,
    showTutorialVideo,
    showWhiteboard,
    activeTool,
    color,
    thickness,
    canvasRef,
    onUnsavedChanges,
    setToolbarExpanded,
    showDisclaimer,
    vetName,
    clinicName,
    token,
    clinicData,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmallScreen = useMediaQuery('(max-width:750px)');
    const appBarHeight = theme.spacing(8);
    const locale = 'en-UK';
    const localeData = handoutData.contentData?.[locale];
    let mainIllustrationRendered = false;

    // console.warn('local data', localeData, handoutData);

    useEffect(() => {
        if (!showWhiteboard) return;
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [showWhiteboard]);

    return (
        <>
            <GlobalStyles
                styles={{
                    '@media print': {
                        '#handout-content': {
                            paddingBottom: '100px',
                        },
                    },
                }}
            />
            <Box id="handout-content" sx={{ my: 2, pb: isMobile ? appBarHeight : '0px', position: 'relative' }}>
                {localeData.map((section, index) => generateSection(section, index))}
                {showDisclaimer && <DisclaimerNotice vetName={vetName} clinicName={clinicName} />}
                <Copyright clinicData={clinicData} />
            </Box>
        </>
    );

    function generateSection(jsonContent, index) {
        const sectionName = jsonContent.sectionName;
        const isPrintPageBreak = sectionName === 'printPageBreak';
        let blocks = jsonContent.blocks;

        // Apply visibility filters
        blocks = blocks.filter((block) => {
            if (block.type === '3dModel' && !show3DModel) return false;
            if (block.type === 'illustration' && !showMainIllustration) return false;
            if (block.type === 'tutorialVideo' && !showTutorialVideo) return false;
            return true;
        });

        if (blocks.length === 0) return null;

        return (
            <React.Fragment key={index}>
                <Box
                    component="section"
                    className={`${sectionName}${isPrintPageBreak ? ' printPageBreak' : ''}`}
                    sx={{
                        my: 4,
                        '@media print': {
                            pageBreakBefore: isPrintPageBreak ? 'auto' : 'avoid',
                            position: 'relative',
                            zIndex: 1,
                        },
                    }}
                >
                    {sectionName === 'diagram' && blocks && blocks[0] && (
                        <FormControlLabel
                            control={<AntSwitch checked={showLabels} onChange={() => setShowLabels((prev) => !prev)} />}
                            label="Labels"
                            labelPlacement="start"
                            sx={{
                                position: 'absolute',
                                top: isSmallScreen ? '-24px' : '16px',
                                right: 0,
                                '& .MuiFormControlLabel-label': { marginRight: '8px' },
                            }}
                        />
                    )}
                    {blocks.map((block, index) => (
                        <React.Fragment key={index}>
                            {blockToComponentMapping(block, sectionName, index)}
                        </React.Fragment>
                    ))}
                </Box>
                {isPrintPageBreak && <div></div>}
            </React.Fragment>
        );
    }

    function blockToComponentMapping(block, sectionName, index) {
        if (!block || !block.type) return null;

        switch (block.type) {
            case 'documentTitle':
                return (
                    <Typography component="h1" key={block.value} variant="documentTitle">
                        {block.value}
                    </Typography>
                );
            case 'sectionTitle':
                return (
                    <Typography component="h2" key={block.value} variant="sectionTitle" sx={{ my: 2 }}>
                        {block.value}
                    </Typography>
                );
            case 'sectionSubtitle':
                return (
                    <Typography component="h3" key={block.value} variant="sectionSubtitle" sx={{ my: 2 }}>
                        {block.value}
                    </Typography>
                );
            case 'calloutSmall':
            case 'calloutMedium':
                return <Callout key={block.value}>{block.value}</Callout>;
            case 'paragraph':
                return (
                    <Typography
                        key={block.value}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            pb: 1,
                            '@media print': {
                                pageBreakInside: 'auto',
                                pageBreakBefore: 'auto',
                                display: 'inline',
                            },
                        }}
                    >
                        {block.value}
                    </Typography>
                );
            case 'list':
                return <BulletList items={block.values} />;
            case 'numberList':
                return <NumberList items={block.values} />;
            case 'iconStrip':
                return (
                    <IconStrip
                        isIframe={true}
                        iconDetails={block.iconDetails}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            '@media print': {
                                pageBreakInside: 'avoid',
                                pageBreakBefore: 'auto',
                                pb: 1,
                                display: 'flex',
                            },
                        }}
                    />
                );
            case 'illustration': {
                console.warn('illustration block...', block);

                const isDiagramSection = sectionName === 'diagram';
                const isDisplayedBlock = isDiagramSection ? index === (showLabels ? 0 : 1) : !mainIllustrationRendered;

                if (!isDisplayedBlock) return null;
                if (!isDiagramSection) mainIllustrationRendered = true;

                return (
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <HandoutImageLoader
                            src={block.src}
                            alt={block.alt}
                            embeddedImgSrc={block.src}
                            token={token}
                            id="main-illustration-img"
                        />

                        {showWhiteboard && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                <Whiteboard
                                    backgroundImage={block.src}
                                    showWhiteboard={showWhiteboard}
                                    activeTool={activeTool}
                                    color={color}
                                    thickness={thickness}
                                    canvasRef={canvasRef}
                                    onUnsavedChanges={onUnsavedChanges}
                                    setToolbarExpanded={setToolbarExpanded}
                                />
                            </Box>
                        )}
                    </Box>
                );
            }
            case '3dModel':
                return (
                    <Model3DViewer uid={block.uid} alt={block.alt} author={block.author} authorWeb={block.authorWeb} />
                );
            case 'tutorialVideo':
                return (
                    <Box key={block.src} sx={{ my: 2 }}>
                        <video width="100%" controls>
                            <source src={block.src} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </Box>
                );
            case 'printPageBreak':
                return (
                    <>
                        <div className="printPageBreak"></div>
                        <p></p>
                    </>
                );
            default:
                return null;
        }
    }
}

export default DisplayHandoutEmbed;
